/**
 * @license
 * @copyright Copyright Motili Inc., 2024 All Rights Reserved
 */
import {
    ReactNode,
    useLayoutEffect,
    useRef,
    useState,
    useEffect,
    CSSProperties,
} from 'react';
import { createPortal } from 'react-dom';
import { styled } from 'styled-components';
import classes from 'common/components-v2/modal/Drawer.module.scss';
import { ClickAwayListener, ClickAwayListenerProps } from '@mui/material';

interface DrawerWithPortalProps {
    children: ReactNode;
    title: ReactNode;
    open: boolean;
    anchor: 'left' | 'right';
    container: Element | DocumentFragment;
    onClose?: () => void;
    showDrawerOverlay?: boolean;
    showCloseIcon?: boolean;
    closeByDocument?: boolean;
    customDrawerStyles?: CSSProperties;
    customOverlayStyles?: CSSProperties;
    mouseEvent?: ClickAwayListenerProps['mouseEvent'];
}

export default function DrawerWithPortal(
    props: Readonly<DrawerWithPortalProps>
) {
    const {
        title,
        open,
        anchor,
        onClose,
        container,
        customDrawerStyles,
        customOverlayStyles,
        showDrawerOverlay = true,
        showCloseIcon = true,
        mouseEvent = 'onClick',
    } = props;
    const { animate, hidden, overlayOpen, overlayHidden } = classes;
    const headerRef = useRef<HTMLDivElement | null>(null);
    const drawerRef = useRef<HTMLDivElement | null>(null);
    const [isScrolled, setIsScrolled] = useState(false);

    const changeAnchor = (anchor: 'left' | 'right') => {
        switch (anchor) {
            case 'left':
                return classes.left;
            case 'right':
                return classes.right;
            default:
                return classes.left;
        }
    };

    useLayoutEffect(() => {
        const refCurrent = drawerRef.current;
        const handleScroll = () => {
            if (refCurrent) {
                const scrollTop = refCurrent.scrollTop;
                setIsScrolled(scrollTop > 0);
            }
        };

        if (refCurrent) {
            refCurrent.addEventListener('scroll', handleScroll);
        }

        return () => {
            if (refCurrent) {
                refCurrent.removeEventListener('scroll', handleScroll);
            }
        };
    }, [open]);

    useEffect(() => {
        if (!open) {
            setIsScrolled(false); // Reset scroll state when the drawer closes
        }
    }, [open]);

    return (
        <>
            {createPortal(
                <>
                    {showDrawerOverlay && (
                        <DrawerOverlay
                            className={`${open ? overlayOpen : overlayHidden}`}
                            style={customOverlayStyles}
                            isOpen={open}
                        />
                    )}
                    {open && (
                        <ClickAwayListener
                            onClickAway={onClose || (() => {})}
                            mouseEvent={mouseEvent}
                        >
                            <Drawer
                                className={`${
                                    open ? animate : hidden
                                } ${changeAnchor(anchor)}`}
                                ref={drawerRef}
                                style={customDrawerStyles}
                            >
                                <DrawerHeader
                                    ref={headerRef}
                                    className={
                                        open && isScrolled ? 'scrolled' : ''
                                    }
                                >
                                    <DrawerTitle>{title}</DrawerTitle>
                                    {showCloseIcon && (
                                        <CloseIconContainer>
                                            <CloseIcon
                                                data-test-id='CloseIcon'
                                                onClick={onClose}
                                                className='fa fa-angle-times'
                                            />
                                        </CloseIconContainer>
                                    )}
                                </DrawerHeader>
                                <DrawerContent>{props.children}</DrawerContent>
                            </Drawer>
                        </ClickAwayListener>
                    )}
                </>,
                container
            )}
        </>
    );
}

const DrawerOverlay = styled.div<{ isOpen: boolean }>`
    visibility: ${props => (props.isOpen ? 'visible' : 'hidden')};
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    opacity: 1;
    z-index: 10;
    background-color: rgba(0, 0, 0, 0.5);
    transition: opacity 225ms cubic-bezier(0.4, 0, 0.2, 1);
`;

const DrawerHeader = styled.div`
    height: fit-content;
    position: sticky;
    background-color: white;
    z-index: 1201;
    width: 100%;
    top: 0;
    transition: box-shadow 0.3s ease-in-out;
    &.scrolled {
        box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    }
`;

const DrawerTitle = styled.div`
    margin: 15px 25px;
    font-size: 22px;
`;

const Drawer = styled.div`
    overflow: auto;
    position: absolute;
    background-color: white;
    top: 0;
    flex: 1 0 auto;
    height: 100%;
    display: flex;
    outline: 0;
    z-index: 1200;
    flex-direction: column;
    width: 50%;
    flex-shrink: 0;
    transition:
        box-shadow 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,
        transform 225ms cubic-bezier(0, 0, 0.2, 1) 0ms;
`;

const DrawerContent = styled.div`
    padding: 10px 25px;
`;

const CloseIconContainer = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
`;

const CloseIcon = styled.i`
    position: absolute;
    right: 25px;
    top: 16px;
    font-size: 25px;
    cursor: pointer;
    color: black;
`;
